import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getSolutionTree, type SolutionCategory } from "@/api/solutionApi";
import { assetUrl } from "@/utils/assetUrl";

const categoryColors: Record<string, string> = {
  "info-security": "#1a56db",
  "it-infrastructure": "#0891b2",
  "intelligent-building": "#059669",
};

export default function SolutionsSection() {
  const [solutionCategories, setSolutionCategories] = useState<SolutionCategory[]>([]);

  useEffect(() => {
    getSolutionTree().then((res: any) => setSolutionCategories(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  // 从所有分类中收集子方案，附带父分类信息，取前3个
  const subItems = solutionCategories.flatMap((cat) =>
    (cat.subSolutions || []).map((sub) => ({
      ...sub,
      categoryId: cat.id,
      categoryTag: cat.tag,
      categoryIcon: cat.icon,
      categoryColor: categoryColors[cat.id] || "#6b7280",
    }))
  ).slice(0, 3);

  return (
    <section className="py-10 lg:py-20 bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
              <span className="text-[#1a56db] text-xs font-medium">核心解决方案</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0B1B3D] mb-3">
              欧格登核心解决方案 · 覆盖企业安全全场景
            </h2>
            <p className="text-gray-500 text-sm max-w-lg">
              从网络边界到数据核心，从IT基础架构到智能楼宇，欧格登智能提供端到端的一体化企业安全解决方案。
            </p>
          </div>
          <Link
            to="/solutions"
            className="flex items-center gap-2 text-[#1a56db] text-sm font-medium hover:gap-3 transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            查看全部解决方案
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>

        {/* Cards — sub solutions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {subItems.map((sub) => (
            <Link
              key={sub.id}
              to={`/solutions/detail/${sub.id}`}
              className="group bg-white rounded-2xl overflow-hidden cursor-pointer block border border-gray-100 hover:border-gray-200 hover:-translate-y-1.5 transition-all duration-300"
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden relative">
                <img
                  src={assetUrl(sub.image)}
                  alt={sub.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <span
                  className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-white text-xs font-medium rounded-full"
                  style={{ backgroundColor: sub.categoryColor }}
                >
                  <i className={`${sub.categoryIcon} text-xs`}></i>
                  {sub.categoryTag}
                </span>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-[#0B1B3D] font-semibold text-base mb-2">{sub.name}</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2">{(sub as any).description || (sub as any).desc || ""}</p>

                {/* Feature tags */}
                <div className="flex flex-wrap gap-1.5">
                  {(sub.features || []).slice(0, 3).map((f) => (
                    <span
                      key={f.name}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F8F9FA] text-gray-500 text-xs rounded-full border border-gray-100"
                    >
                      <i className={`${f.icon} text-xs`}></i>
                      {f.name}
                    </span>
                  ))}
                  {(sub.features || []).length > 3 && (
                    <span className="px-2 py-0.5 bg-[#F8F9FA] text-gray-400 text-xs rounded-full border border-gray-100">
                      +{sub.features.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
